/**
 * Responsive painted rasters (variants made by scripts/image-variants.mjs): pick the smallest width that still covers
 * the box at the screen's pixel density. Phones cap the density at 2 (a 3x bitmap costs 2.25x the memory for detail a
 * child cannot see on a 6" screen); tablets / desktops cap at 3.
 */

/** Device pixel ratio used for images and canvases: 2 on phones (short side < 600 CSS px), else up to 3. */
export function imageDpr(): number {
  if (typeof window === 'undefined') return 1;
  const dpr = window.devicePixelRatio || 1;
  const phone = Math.min(window.innerWidth, window.innerHeight) < 600;
  return Math.min(dpr, phone ? 2 : 3);
}

/** Smallest width in `widths` (ascending) that covers `cssWidth` × dpr; the largest when none does. */
export function pickWidth(widths: readonly number[], cssWidth: number, dpr = imageDpr()): number {
  const need = Math.ceil(cssWidth * dpr);
  return widths.find((w) => w >= need) ?? widths[widths.length - 1]!;
}

/** `<base>.webp` for the full-size source, `<base>-<w>.webp` for a smaller variant. */
export const variantUrl = (base: string, w: number, full: number) => (w >= full ? `${base}.webp` : `${base}-${w}.webp`);

/** World map paintings: 1536 × 704 sources, variants 768 / 1152. */
export const WORLD_WIDTHS = [768, 1152, 1536] as const;
export const worldMapUrl = (world: string, cssWidth: number) => variantUrl(`/worlds/${world}`, pickWidth(WORLD_WIDTHS, cssWidth), 1536);

/** Picture vignettes: 768 × 576 sources, variant 384. */
export const VIGNETTE_WIDTHS = [384, 768] as const;

/** Observe object icons: 384² sources, variant 192 (srcset; the browser picks by `sizes`). */
export const observeSrcSet = (id: string) => `/observe/${id}-192.webp 192w, /observe/${id}.webp 384w`;
